/**
 * Salida y regreso de bodega — el registro que cierra el ciclo de una orden.
 *
 * Al salir: cada renglón (producto, material, herramienta, extra) con la cantidad
 * que sale y el nombre de quien se lo lleva. Queda por escrito que se llevó todo.
 *
 * Al regresar: la cantidad que vuelve por renglón. La diferencia se calcula sola,
 * pero significa cosas distintas según el tipo — el material se consume (lo que
 * vuelve es sobrante), el producto se queda instalado, y la herramienta regresa
 * SIEMPRE: su faltante se resalta con responsable, y se procede.
 *
 * Un registro por cotización (id `bodega-<cotId>`), inmutable en su origen como
 * todo dato comercial; corregir el regreso es volver a capturarlo, no borrarlo.
 */
import { useEffect, useState } from 'react'
import * as store from '../datos/store.js'
import { renglonesDeSalida, NOMBRE_TIPO_RENGLON } from '../motor/bodega.js'
import { BarraSimple } from './Encargo.jsx'

const fechaCorta = iso => (iso || '').slice(0, 16).replace('T', ' · ')

export default function Bodega({ cot, ctx, herramientas, atras }) {
  const id = `bodega-${cot.id}`
  const [registro, setRegistro] = useState(undefined)   // undefined = cargando
  const [borrador, setBorrador] = useState(null)        // renglones editables antes de la salida
  const [responsable, setResponsable] = useState('')
  const [extra, setExtra] = useState({ nombre: '', q: 1 })
  const [confirmando, setConfirmando] = useState(false) // alto con resumen antes de firmar

  useEffect(() => {
    let vivo = true
    store.obtener('bodega', id).then(r => {
      if (!vivo) return
      setRegistro(r)
      if (!r) setBorrador(renglonesDeSalida(cot, ctx, herramientas))
    })
    return () => { vivo = false }
    // La lista generada se congela al abrir: si la cotización cambia después de la
    // salida, el registro histórico no se reescribe solo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (registro === undefined) return (
    <div className="app">
      <BarraSimple titulo="Bodega" migaja={cot.nombre} atras={atras} />
      <main><div className="empty">Cargando…</div></main>
    </div>
  )

  /* ---------- Antes de la salida: armar la carga ---------- */
  if (!registro) {
    const mutar = (i, campo, v) =>
      setBorrador(prev => prev.map((r, j) => (j === i ? { ...r, [campo]: v } : r)))
    const quitar = i => setBorrador(prev => prev.filter((_, j) => j !== i))
    const agregarExtra = () => {
      if (!extra.nombre.trim()) return
      setBorrador(prev => [...prev,
        { tipo: 'extra', clave: '', nombre: extra.nombre.trim(), sale: extra.q || 1, vuelve: 0 }])
      setExtra({ nombre: '', q: 1 })
    }
    const registrarSalida = async () => {
      await store.guardar('bodega', {
        id, cotizacionId: cot.id, responsable: responsable.trim(),
        renglones: borrador.filter(r => r.sale > 0),
        fechaSalida: new Date().toISOString(), fechaRegreso: null
      })
      setRegistro(await store.obtener('bodega', id))
    }
    return (
      <div className="app">
        <BarraSimple titulo="Salida de bodega" migaja={cot.nombre} atras={atras} />
        <main>
          <div className="nota">
            La lista sale de la cotización: sus productos, el material consolidado y las
            herramientas que cada producto declara. Ajusta cantidades, agrega los extras
            de último minuto, y registra la salida con el nombre de quien se lo lleva.
          </div>
          {(borrador || []).map((r, i) => (
            <div className="card" key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="grow">
                <div className="t">{r.nombre}</div>
                <div className="s">{NOMBRE_TIPO_RENGLON[r.tipo] || r.tipo}</div>
              </div>
              <label className="f" style={{ margin: 0, width: 86 }}>
                <span>Sale</span>
                <input type="number" min="0" value={r.sale}
                  onChange={e => mutar(i, 'sale', parseInt(e.target.value, 10) || 0)} />
              </label>
              <button className="ib sm" aria-label={`Quitar ${r.nombre}`}
                onClick={() => quitar(i)}>×</button>
            </div>
          ))}

          <div className="card">
            <div className="two">
              <label className="f"><span>Extra de último minuto</span>
                <input value={extra.nombre} placeholder="Lo que se decida cargar de más"
                  onChange={e => setExtra({ ...extra, nombre: e.target.value })} /></label>
              <label className="f"><span>Cantidad</span>
                <input type="number" min="1" value={extra.q}
                  onChange={e => setExtra({ ...extra, q: parseInt(e.target.value, 10) || 1 })} /></label>
            </div>
            <button className="ib" disabled={!extra.nombre.trim()} onClick={agregarExtra}>
              Agregar a la carga
            </button>
          </div>

          <div className="card">
            <label className="f">
              <span>Quién se lo lleva (nombre — vale como firma de salida)</span>
              <input value={responsable} onChange={e => setResponsable(e.target.value)} />
            </label>
            {/* ALTO CON RESUMEN (Regla 5 de la teoría): la salida vale como firma y su
                origen es inmutable, así que no se registra con un toque suelto. El alto
                es un renglón en la propia pantalla —no un modal— y dice exactamente qué
                se está firmando, en lenguaje llano. */}
            {confirmando ? (() => {
              const carga = (borrador || []).filter(r => r.sale > 0)
              const cuenta = tipo => carga.filter(r => r.tipo === tipo)
                .reduce((a, r) => a + (r.sale || 0), 0)
              const partes = [
                ['producto', 'de producto'], ['material', 'de material'],
                ['herramienta', 'de herramienta'], ['extra', 'de extras']
              ].map(([t, n]) => (cuenta(t) ? `${cuenta(t)} ${n}` : null)).filter(Boolean)
              return (
                <div className="nota acento" style={{ marginTop: 8 }}>
                  <b>Va a quedar registrado, a nombre de {responsable.trim()}:</b>{' '}
                  {carga.length} renglones — {partes.join(', ')}. Este registro vale
                  como firma de salida y no se edita después: corregirlo es capturar
                  el regreso.
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button type="button" className="ib" onClick={() => setConfirmando(false)}>
                      Volver a revisar
                    </button>
                    <button type="button" className="ib pri"
                      onClick={() => { setConfirmando(false); registrarSalida() }}>
                      Sí, registrar la salida
                    </button>
                  </div>
                </div>
              )
            })() : (
              <button type="button" className="ib pri"
                disabled={!responsable.trim() || !(borrador || []).some(r => r.sale > 0)}
                onClick={() => setConfirmando(true)}>
                Registrar la salida
              </button>
            )}
          </div>
        </main>
      </div>
    )
  }

  /* ---------- Con salida registrada: capturar o corregir el regreso ---------- */
  const cerrado = !!registro.fechaRegreso
  const mutarVuelve = (i, v) => setRegistro(prev => ({
    ...prev,
    renglones: prev.renglones.map((r, j) => (j === i ? { ...r, vuelve: v } : r))
  }))
  const registrarRegreso = async () => {
    await store.guardar('bodega', { ...registro, fechaRegreso: new Date().toISOString() })
    setRegistro(await store.obtener('bodega', id))
  }
  const reabrir = async () => {
    await store.guardar('bodega', { ...registro, fechaRegreso: null })
    setRegistro(await store.obtener('bodega', id))
  }

  /* Qué significa la diferencia, por tipo: solo la herramienta genera faltante. */
  const notaDiferencia = r => {
    const dif = (r.sale || 0) - (r.vuelve || 0)
    if (r.tipo === 'herramienta') return dif > 0 ? { texto: `FALTAN ${dif}`, falta: true } : { texto: 'Completo', falta: false }
    if (r.tipo === 'material') return { texto: dif > 0 ? `Consumido: ${dif}` : 'Volvió todo', falta: false }
    return { texto: dif > 0 ? `En obra: ${dif}` : 'Volvió todo', falta: false }
  }
  const faltantes = registro.renglones.filter(r => r.tipo === 'herramienta' && (r.sale || 0) > (r.vuelve || 0))

  return (
    <div className="app">
      <BarraSimple titulo={cerrado ? 'Bodega — ciclo cerrado' : 'Regreso de bodega'}
        migaja={cot.nombre} atras={atras} />
      <main>
        <div className="card"><div className="grow">
          <div className="t">Salió con {registro.responsable || '—'}</div>
          <div className="s">
            Salida: {fechaCorta(registro.fechaSalida)}
            {cerrado && ` · Regreso: ${fechaCorta(registro.fechaRegreso)}`}
          </div>
        </div></div>

        {cerrado && faltantes.length > 0 && (
          <div className="warn">
            <b>No regresó toda la herramienta.</b> {faltantes.map(r =>
              `${r.nombre} (${(r.sale || 0) - (r.vuelve || 0)})`).join(' · ')} —
            responsable: {registro.responsable || 'sin registrar'}.
          </div>
        )}

        {registro.renglones.map((r, i) => {
          const n = notaDiferencia(r)
          return (
            <div className="card" key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="grow">
                <div className="t">{r.nombre}</div>
                <div className="s">
                  {NOMBRE_TIPO_RENGLON[r.tipo] || r.tipo} · salió {r.sale}
                  {cerrado && <> · <b style={n.falta ? { color: 'var(--alto)' } : undefined}>{n.texto}</b></>}
                </div>
              </div>
              {!cerrado && (
                <label className="f" style={{ margin: 0, width: 96 }}>
                  <span>Vuelve</span>
                  <input type="number" min="0" value={r.vuelve ?? 0}
                    onChange={e => mutarVuelve(i, parseInt(e.target.value, 10) || 0)} />
                </label>
              )}
            </div>
          )
        })}

        {!cerrado ? (
          <div className="card">
            <div className="grow">
              <div className="t">Cerrar el ciclo</div>
              <div className="s">
                El material que vuelve es sobrante; el producto que no vuelve quedó instalado.
                <b> Las herramientas tienen que volver todas</b> — lo que falte queda resaltado
                con responsable.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="ib" onClick={() => setRegistro(prev => ({
                ...prev,
                renglones: prev.renglones.map(r =>
                  r.tipo === 'herramienta' ? { ...r, vuelve: r.sale } : r)
              }))}>
                Volvió toda la herramienta
              </button>
              <button className="ib pri" onClick={registrarRegreso}>Registrar el regreso</button>
            </div>
          </div>
        ) : (
          <button className="ib" onClick={reabrir}>Corregir el regreso</button>
        )}
      </main>
    </div>
  )
}
